import { DrofusChangesLoadingOverlay } from "./DrofusChangesLoadingOverlay";
import "ag-grid-enterprise";
import { DrofusOccurrenceChangesTable } from "./DrofusOccurrenceChangesTable";
import { Badge, Box, Button, Group, Text } from "@mantine/core";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { skipToken } from "@reduxjs/toolkit/query";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { useMemo, useRef, useState } from "react";
import { FaFileExcel } from "react-icons/fa6";
import {
  openapi,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiResponse,
} from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DrofusDisciplineBadge } from "../components/DrofusDisciplineBadge";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import { DrofusForekomstDetailsModal } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { closeDrofusEndringerOccurrenceDetails } from "./drofusEndringerSlice";
import {
  selectDrofusEndringerRoomGroupsApiArg,
  selectDrofusEndringerRoomGroupsSummary,
  selectDrofusEndringerSelectedOccurrenceRef,
} from "./drofusEndringerSelectors";

type DrofusRoomGroupRow =
  PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiResponse["items"][number];
function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

const DisciplinesRenderer: React.FC<ICellRendererParams<unknown, string[]>> = ({ value }) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <DrofusDisciplineBadge key={discipline} discipline={discipline} />
      ))}
    </Group>
  );
};

const CountSummaryRenderer: React.FC<ICellRendererParams<DrofusRoomGroupRow>> = ({ data }) => {
  if (!data) return null;
  return (
    <Group gap={6} wrap="nowrap">
      <Badge size="sm" radius="sm" variant="light" color="gray">
        {data.changeCount}
      </Badge>
      {data.updatedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="yellow">
          E {data.updatedCount}
        </Badge>
      )}
      {data.addedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="green">
          N {data.addedCount}
        </Badge>
      )}
      {data.removedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="red">
          F {data.removedCount}
        </Badge>
      )}
    </Group>
  );
};

const RoomNumberRenderer: React.FC<
  ICellRendererParams<DrofusRoomGroupRow, string | null> & {
    onOpenRoom?: (roomNumber: string) => void;
  }
> = ({ value, onOpenRoom }) => {
  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenRoom?.(value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

const RoomChangesDetailRenderer: React.FC<{ data?: DrofusRoomGroupRow }> = ({ data }) => {
  const apiArg = useAppSelector(selectDrofusEndringerRoomGroupsApiArg);
  const queryKey = JSON.stringify([apiArg, data?.roomFunctionNo]);
  const [pagination, setPagination] = useState({ queryKey, page: 1 });
  const page = pagination.queryKey === queryKey ? pagination.page : 1;
  const {
    currentData: details,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsQuery(
    apiArg && data
      ? {
          body: {
            ...apiArg.body,
            roomFunctionNo: data.roomFunctionNo,
            limit: 100,
            offset: (page - 1) * 100,
          },
        }
      : skipToken
  );
  return (
    <Box h={440} p="xs">
      <DrofusOccurrenceChangesTable
        items={details?.items ?? []}
        total={details?.total ?? data?.changeCount ?? 0}
        offset={(page - 1) * 100}
        limit={100}
        isLoading={isLoading || (isFetching && !details)}
        isFetching={isFetching}
        isError={isError}
        enabled={Boolean(apiArg && data)}
        stateKey="drofus-endringer-room-details"
        showRoomColumn={false}
        onPageChange={(page) => setPagination({ queryKey, page })}
        onRetry={refetch}
      />
    </Box>
  );
};

export const DrofusEndringerGroupedByRoomTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<AgGridReact<DrofusRoomGroupRow>>(null);
  const apiArg = useAppSelector(selectDrofusEndringerRoomGroupsApiArg);
  const summary = useAppSelector(selectDrofusEndringerRoomGroupsSummary);
  const selectedOccurrenceRef = useAppSelector(selectDrofusEndringerSelectedOccurrenceRef);
  const { data, isLoading, isFetching, isError } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsByRoomQuery(apiArg ?? skipToken);

  const rowData = data?.items ?? [];
  const columnDefs = useMemo<ColDef<DrofusRoomGroupRow>[]>(
    () => [
      {
        headerName: "Romnummer",
        field: "roomFunctionNo",
        width: 150,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: "RoomNumberRenderer",
          innerRendererParams: {
            onOpenRoom: (roomNumber: string) => {
              dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_DROFUS_CHANGES));
              dispatch(setSelectedSpaceNumberInRedux(roomNumber));
            },
          },
        },
      },
      {
        headerName: "Romnavn",
        field: "roomName",
        minWidth: 220,
        flex: 1,
      },
      {
        headerName: "Bygg",
        field: "roomBuilding",
        width: 110,
      },
      {
        headerName: "Etasje",
        field: "roomFloor",
        width: 130,
      },
      {
        headerName: "Siste endring",
        field: "latestRunStartedAt",
        width: 160,
        valueFormatter: ({ value }: ValueFormatterParams<DrofusRoomGroupRow, string>) =>
          formatDateTime(value),
      },
      {
        headerName: "Endringer",
        colId: "changeSummary",
        width: 210,
        cellRenderer: "CountSummaryRenderer",
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        width: 170,
        cellRenderer: "DisciplinesRenderer",
      },
    ],
    [dispatch]
  );

  const handleExportExcel = () => {
    gridRef.current?.api.exportDataAsExcel({
      fileName: `drofus-endringer-rom-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Rom",
      processCellCallback: (params) => {
        const colId = params.column.getColId();

        if (colId === "latestRunStartedAt") return formatDateTime(params.value);
        if (colId === "affectedDisciplines" && Array.isArray(params.value)) {
          return params.value.join(", ");
        }
        if (colId === "changeSummary") {
          const row = params.node?.data;
          if (!row) return "";

          return [
            `${row.changeCount} totalt`,
            row.updatedCount > 0 ? `${row.updatedCount} endret` : null,
            row.addedCount > 0 ? `${row.addedCount} ny` : null,
            row.removedCount > 0 ? `${row.removedCount} fjernet` : null,
          ]
            .filter(Boolean)
            .join(", ");
        }

        return formatValue(params.value);
      },
    });
  };

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DrofusForekomstDetailsModal
        occurrenceRef={selectedOccurrenceRef}
        opened={selectedOccurrenceRef != null}
        onClose={() => dispatch(closeDrofusEndringerOccurrenceDetails())}
      />

      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text size="sm" c={isError ? "red" : "dimmed"} style={{ whiteSpace: "nowrap" }}>
          {isError
            ? "Kunne ikke laste romgrupper"
            : `${summary.firstVisibleRow}-${summary.lastVisibleRow} av ${summary.total} rom`}
        </Text>
        <Group gap="xs" wrap="nowrap">
          {isFetching && (
            <Badge variant="light" color="blue" radius="sm">
              Laster
            </Badge>
          )}
          <Button
            size="xs"
            color="green"
            variant="light"
            radius="sm"
            leftSection={<FaFileExcel size={14} />}
            disabled={!apiArg || isLoading || rowData.length === 0}
            onClick={handleExportExcel}
          >
            Excel
          </Button>
        </Group>
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<DrofusRoomGroupRow>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: true,
            minWidth: 120,
          }}
          loading={isLoading || isFetching}
          loadingOverlayComponent={DrofusChangesLoadingOverlay}
          animateRows
          masterDetail
          detailRowAutoHeight
          isRowMaster={(row) => (row?.changeCount ?? 0) > 0}
          detailCellRenderer={RoomChangesDetailRenderer}
          getRowId={({ data }) => data.roomFunctionNo ?? "uten-rom"}
          suppressScrollOnNewData
          tooltipShowDelay={150}
          components={{
            CountSummaryRenderer,
            DisciplinesRenderer,
            RoomNumberRenderer,
          }}
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen rom med endringer funnet</span>"
        />
      </Box>

      <Text size="sm" c="dimmed" mt="sm">
        {summary.total} rom
      </Text>
    </Box>
  );
};
